import React, { useState, useEffect, useCallback } from 'react';
import { aiTemplatesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Search, Download, Star, Check, Loader2, Sparkles, Brain,
  Zap, Mail, Phone, Target, Building2, RotateCcw, Reply, Heart, Sprout,
  HandMetal, PhoneCall, X, BarChart3, Plus, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import AITemplateAnalytics from '../AITemplateAnalytics';
import TemplateEditorDialog from './TemplateEditorDialog';

const ICON_MAP = { Zap, Mail, Phone, Target, Building2, RotateCcw, Reply, HandMetal, PhoneCall, Heart, Sprout };
const CATEGORIES = [
  { id: 'all', label: 'All', icon: Sparkles },
  { id: 'cadence', label: 'Cadences', icon: Zap },
  { id: 'email', label: 'Emails', icon: Mail },
  { id: 'voice_script', label: 'Voice', icon: Phone },
  { id: 'agent_preset', label: 'Agent', icon: Brain },
];

const CAT_COLORS = {
  cadence: 'border-violet-500/20 from-violet-500/10',
  email: 'border-blue-500/20 from-blue-500/10',
  voice_script: 'border-emerald-500/20 from-emerald-500/10',
  agent_preset: 'border-amber-500/20 from-amber-500/10',
};

const CAT_BADGE = {
  cadence: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  email: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  voice_script: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  agent_preset: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

export default function TemplateHub() {
  const [templates, setTemplates] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [installingId, setInstallingId] = useState(null);
  const [tab, setTab] = useState('browse');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search) params.search = search;
      const [tplRes, instRes] = await Promise.all([
        aiTemplatesAPI.getAll(params),
        aiTemplatesAPI.getInstalled(),
      ]);
      setTemplates(tplRes.data?.templates || tplRes.data || []);
      setInstalled(instRes.data?.templates || instRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleInstall = async (id) => {
    setInstallingId(id);
    try {
      await aiTemplatesAPI.install(id);
      toast.success('Template installed');
      fetchTemplates();
    } catch { toast.error('Install failed'); }
    finally { setInstallingId(null); }
  };

  const handleUninstall = async (id) => {
    setInstallingId(id);
    try {
      await aiTemplatesAPI.uninstall(id);
      toast.success('Template uninstalled');
      fetchTemplates();
    } catch { toast.error('Uninstall failed'); }
    finally { setInstallingId(null); }
  };

  const installedIds = new Set(installed.map(t => t.id));

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (tpl) => { setEditing(tpl); setEditorOpen(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await aiTemplatesAPI.deleteCustom(deleteTarget.id);
      toast.success('Template deleted');
      setDeleteTarget(null);
      fetchTemplates();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="template-hub-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="template-hub-title">AI Template Hub</h1>
          <p className="text-muted-foreground text-sm">Install templates to supercharge your Brain's capabilities</p>
        </div>
        <Button onClick={openCreate} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Create template
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="template-tabs">
          <TabsTrigger value="browse" data-testid="tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="installed" data-testid="tab-installed">
            Installed <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5">{installed.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">
            <BarChart3 className="w-3.5 h-3.5 mr-1" /> Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-4 mt-4">
          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search templates..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="template-search" />
            </div>
            <div className="flex gap-1.5 overflow-x-auto">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <Button key={cat.id} variant={category === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setCategory(cat.id)} className="text-xs shrink-0" data-testid={`filter-${cat.id}`}>
                    <Icon className="h-3 w-3 mr-1" /> {cat.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Template Grid */}
          {loading ? (
            <div className="space-y-3 py-4" data-testid="template-hub-skeleton">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg border space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="h-5 w-48 bg-muted rounded animate-pulse" />
                      <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
                    </div>
                    <div className="h-7 w-20 bg-muted/60 rounded animate-pulse" />
                  </div>
                  <div className="flex gap-2">
                    <div className="h-5 w-16 bg-muted/40 rounded-full animate-pulse" />
                    <div className="h-5 w-20 bg-muted/40 rounded-full animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <Card><CardContent className="text-center py-12 text-muted-foreground text-sm">No templates found</CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="template-grid">
              {templates.map(tpl => (
                <TemplateCard key={tpl.id} template={tpl} isInstalled={installedIds.has(tpl.id) || tpl.installed} installing={installingId === tpl.id} onInstall={() => handleInstall(tpl.id)} onUninstall={() => handleUninstall(tpl.id)} onEdit={() => openEdit(tpl)} onDelete={() => setDeleteTarget(tpl)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="installed" className="space-y-4 mt-4">
          {installed.length === 0 ? (
            <Card><CardContent className="text-center py-12 text-muted-foreground text-sm"><Brain className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No templates installed. Browse and install templates to enhance your Brain.</p></CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="installed-grid">
              {installed.map(tpl => (
                <TemplateCard key={tpl.id} template={tpl} isInstalled={true} installing={installingId === tpl.id} onInstall={() => {}} onUninstall={() => handleUninstall(tpl.id)} onEdit={() => openEdit(tpl)} onDelete={() => setDeleteTarget(tpl)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="performance" className="mt-4" data-testid="performance-tab-content">
          <AITemplateAnalytics />
        </TabsContent>
      </Tabs>

      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
        onSaved={fetchTemplates}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be permanently removed and unavailable to campaigns. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-template-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="delete-template-confirm">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TemplateCard({ template, isInstalled, installing, onInstall, onUninstall, onEdit, onDelete }) {
  const Icon = ICON_MAP[template.icon] || Sparkles;
  const gradient = CAT_COLORS[template.category] || 'border-border from-muted/10';
  const badgeColor = CAT_BADGE[template.category] || 'bg-muted text-muted-foreground';

  return (
    <Card className={`group relative overflow-hidden border bg-gradient-to-br ${gradient} to-transparent hover:shadow-md transition-all`} data-testid={`template-card-${template.id}`}>
      {template.is_featured && (
        <div className="absolute top-2.5 right-2.5">
          <Badge className="bg-amber-500/90 text-white text-[9px] px-1.5 py-0 border-0"><Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> Featured</Badge>
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-background/80 border border-border/50 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-sm font-semibold leading-tight">{template.name}</CardTitle>
            <div className="flex items-center gap-1 mt-1">
              <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${badgeColor} border-0`}>
                {template.category === 'voice_script' ? 'Voice' : template.category === 'agent_preset' ? 'Agent' : template.category}
              </Badge>
              {template.is_custom && (
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 border-0" data-testid={`custom-badge-${template.id}`}>Custom</Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{template.description}</p>
        <div className="flex flex-wrap gap-1">
          {(template.tags || []).slice(0, 3).map(tag => (
            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-background/60 text-muted-foreground border border-border/30">{tag}</span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-border/30">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Download className="w-3 h-3" /> {template.install_count}</span>
            {template.rating > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {template.rating}</span>}
          </div>
          {isInstalled ? (
            <div className="flex items-center gap-1.5">
              {template.is_custom && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onEdit} data-testid={`edit-${template.id}`} title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={onDelete} data-testid={`delete-${template.id}`} title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onUninstall} disabled={installing} data-testid={`uninstall-${template.id}`}>
                {installing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><X className="h-3 w-3 mr-1" /> Remove</>}
              </Button>
            </div>
          ) : (
            <Button size="sm" className="h-7 text-xs" onClick={onInstall} disabled={installing} data-testid={`install-${template.id}`}>
              {installing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Install</>}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
